/**
 * Release Notes Static Data (Issue #933)
 *
 * Phase 1: Static TypeScript data file.
 * Phase 2: Will be replaced by API call to /api/content/release-notes
 *
 * Content matches docs/canonical/public/RELEASE_NOTES_v*.md files.
 *
 * v1.8.0 (Lumen) re-cut: the in-app Release Notes screen ships a single,
 * first-release-style entry, re-versioned at each cut (v1.8.1, v1.9.0, v1.9.1,
 * v1.9.2, v1.10.0, v1.11.0, v1.12.0). Historical public MD notes are archived under
 * docs/canonical/public/archive/ (out of the sync gate's maxdepth-1 glob).
 *
 * v1.9.2: the entry moved from bullet inventory back to short prose in the
 * house voice (owner decision 2026-09-09; the bullets were a side effect of the
 * v1.8.0 re-cut, not a recorded voice change). A first-time iPhone user reads
 * this as a description of the product, never as a changelog; fixes live in
 * docs/releases/v*-release-notes.md only.
 */

import type { ReleaseNote } from '../types/cms';

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: 'v1.12.0',
    codename: 'Lumen',
    releaseDate: 'October 2026',
    isCurrent: true,
    summary:
      'ORO move™ is your club in one place: the people, the events, and the conversation. No feeds, no algorithms, no ads.',
    sections: [
      {
        id: 'your-club',
        title: 'Your club',
        blocks: [
          {
            type: 'paragraph',
            text: 'A club is its members, its events, and a calendar. Make it public, invite-only, or private. Need a hand? Promote a few leaders to help you run it.',
          },
        ],
      },
      {
        id: 'chat',
        title: 'Chat',
        blocks: [
          {
            type: 'paragraph',
            text: 'Every club gets a room. So does every event and every series, so the plan for Saturday does not bury the plan for the season. React to a message with a tap. Leaders can pin one message per room.',
          },
        ],
      },
      {
        id: 'events',
        title: 'Events',
        blocks: [
          {
            type: 'paragraph',
            text: 'Give an event a date, a place, a cover photo, and whatever else it needs. RSVP yes, maybe, or no, and change your mind later. Leaders can see who is coming and check people in when they arrive.',
          },
        ],
      },
      {
        id: 'keeping-it-civil',
        title: 'Keeping it civil',
        blocks: [
          {
            type: 'paragraph',
            text: 'Block anyone from their profile. Their messages and activity disappear for you, and yours for them. Report a message or a profile from its menu and it drops out of your view while we look. We review reports within 24 hours and remove content or accounts that cross the line.',
          },
          {
            type: 'paragraph',
            text: 'Club chat stays inside the club. ORO admins see a message if someone reports it. Your block list lives in Settings → Blocked Users, in case you change your mind.',
          },
        ],
      },
      {
        id: 'signing-in',
        title: 'Signing in',
        blocks: [
          {
            type: 'paragraph',
            text: 'No password, ever. We send a link to your email or a code to your phone, and you are in. The first time you sign in, you are prompted to agree to our Terms and Privacy Policy, and you can read both right there.',
          },
        ],
      },
      {
        id: 'about-our-texts',
        title: 'About our texts',
        blocks: [
          {
            type: 'paragraph',
            text: 'Reply STOP and our texts stop, sign-in codes included. Text START to turn them back on. Pick which texts you get in Settings → Notifications. If a sign-in code has not arrived, you can ask for another after 30 seconds.',
          },
        ],
      },
      {
        id: 'on-your-phone',
        title: 'On your phone',
        blocks: [
          {
            type: 'paragraph',
            text: 'ORO move is on the App Store for iPhone, worldwide. It works in a browser too, on any phone, tablet, or laptop, and it is the same club and the same account either way. oro.fit/app shows you around before you download.',
          },
        ],
      },
      {
        id: 'new-faces',
        title: 'New faces',
        blocks: [
          {
            type: 'paragraph',
            text: 'New to a club? Event pages now show a First time? card with where to meet and the pace groups to choose from. In a club that is not public, Bring a friend on the event page shares your invite link, and your friend lands on that event once they join. It shows up when the club lets you invite people.',
          },
        ],
      },
    ],
  },
];
